import { useEffect, useState, useCallback, useRef } from 'react';
import { dashboardApi, eventsApi, quotationsApi, enquiriesApi, tasksApi, clientPaymentsApi } from '@/lib/api';
import { extractStatsData, extractArrayData, extractChartData, validateDashboardData } from '@/lib/dashboardDataExtractor';

// Get start and end of current month in local YYYY-MM-DD format
const getCurrentMonthRange = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  
  const from_date = `${year}-${String(month + 1).padStart(2, '0')}-01`;
  const lastDay = new Date(year, month + 1, 0).getDate();
  const to_date = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  
  return { from_date, to_date };
};

interface DashboardData {
  stats: any;
  chartData: any[];
  events: any[];
  quotations: any[];
  enquiries: any[];
  tasks: any[];
  lastUpdated: Date;
}

interface UseDashboardDataOptions {
  refreshInterval?: number; // milliseconds
  autoRefresh?: boolean;
}

export function useDashboardData(options: UseDashboardDataOptions = {}) {
  const { refreshInterval = 30000, autoRefresh = true } = options;
  const [data, setData] = useState<DashboardData>({
    stats: null,
    chartData: [],
    events: [],
    quotations: [],
    enquiries: [],
    tasks: [],
    lastUpdated: new Date(),
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const fetchDashboardData = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);
      
      // Fetch all data in parallel
      const [statsRes, chartRes, eventsRes, quotationsRes, enquiriesRes, tasksRes, clientPaymentsRes] = await Promise.all([
        dashboardApi.getOverview().catch((err) => {
          console.error('Overview error:', err);
          return null;
        }),
        dashboardApi.getStats().catch((err) => {
          console.error('Stats error:', err);
          return null;
        }),
        eventsApi.list('limit=5&sort=-date').catch((err) => {
          console.error('Events error:', err);
          return null;
        }),
        quotationsApi.list('limit=5&sort=-created_at').catch((err) => {
          console.error('Quotations error:', err);
          return null;
        }),
        enquiriesApi.list('limit=5&sort=-created_at').catch((err) => {
          console.error('Enquiries error:', err);
          return null;
        }),
        tasksApi.list('limit=5&sort=-due_date').catch((err) => {
          console.error('Tasks error:', err);
          return null;
        }),
        clientPaymentsApi.list(getCurrentMonthRange()).catch((err) => {
          console.error('Client payments error:', err);
          return null;
        }),
      ]);

      // Safely extract data from responses using extractors
      const statsData = extractStatsData(statsRes);
      const chartData = extractChartData(chartRes); // Chart data comes from stats response (chartRes)
      const eventsData = extractArrayData(eventsRes);
      const quotationsData = extractArrayData(quotationsRes);
      const enquiriesData = extractArrayData(enquiriesRes);
      const tasksData = extractArrayData(tasksRes);
      
      // Get proper expense breakdown from stats endpoint
      // statsData.expenses.operational = actual expenses from Expense table
      // statsData.expenses.vendor_payments = payments to vendors
      const operationalExpenses = statsData.expenses?.operational || 0;
      const vendorPayments = statsData.expenses?.vendor_payments || 0;
      const totalActualExpenses = operationalExpenses + vendorPayments;
      
      // Calculate total revenue from client payments
      let totalRevenueFromPayments = 0;
      const paymentsData = extractArrayData(clientPaymentsRes);
      if (paymentsData && paymentsData.length > 0) {
        totalRevenueFromPayments = paymentsData.reduce((sum: number, payment: any) => {
          const amount = Number(payment.amount) || 0;
          return sum + amount;
        }, 0);
      }

      // Override total_revenue with client payments total and use actual expenses
      const updatedStats = {
        ...statsData,
        total_revenue: totalRevenueFromPayments || statsData.total_revenue || 0,
        client_payments_total: totalRevenueFromPayments,
        total_payments_count: paymentsData?.length || 0,
        // Use actual expenses breakdown instead of mixed total_cost
        total_cost: totalActualExpenses,
        operational_expenses: operationalExpenses,
        vendor_payments: vendorPayments,
      };

      console.log('Dashboard data loaded:', {
        stats: updatedStats,
        chartData: chartData?.length || 0,
        events: eventsData?.length || 0,
        quotations: quotationsData?.length || 0,
        enquiries: enquiriesData?.length || 0,
        tasks: tasksData?.length || 0,
        clientPayments: paymentsData?.length || 0,
        totalRevenueFromPayments,
      });

      const newData = {
        stats: updatedStats,
        chartData: chartData,
        events: eventsData,
        quotations: quotationsData,
        enquiries: enquiriesData,
        tasks: tasksData,
        lastUpdated: new Date(),
      };

      // Validate data before setting
      const validatedData = validateDashboardData(newData);
      setData(validatedData);

      setLoading(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch dashboard data';
      console.error('Dashboard fetch error:', errorMessage, err);
      setError(errorMessage);
      setLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Auto-refresh
  useEffect(() => {
    if (!autoRefresh) return;

    refreshTimeoutRef.current = setInterval(() => {
      fetchDashboardData();
    }, refreshInterval);

    return () => {
      if (refreshTimeoutRef.current) {
        clearInterval(refreshTimeoutRef.current);
      }
    };
  }, [autoRefresh, refreshInterval, fetchDashboardData]);

  const refresh = useCallback(() => {
    setLoading(true);
    fetchDashboardData();
  }, [fetchDashboardData]);

  return { data, loading, error, refresh };
}

// Hook for real-time metrics with polling
export function useDashboardMetrics(refreshInterval: number = 10000) {
  const [metrics, setMetrics] = useState({
    activeEvents: 0,
    pendingQuotations: 0,
    totalRevenue: 0,
    pendingPayments: 0,
    completedTasks: 0,
    totalClients: 0,
  });
  const [loading, setLoading] = useState(true);
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const fetchMetrics = useCallback(async () => {
    try {
      const [overview, stats, clientPaymentsRes] = await Promise.all([
        dashboardApi.getOverview().catch(() => null),
        dashboardApi.getStats().catch(() => null),
        clientPaymentsApi.list(getCurrentMonthRange()).catch(() => null),
      ]);

      const overviewData = extractStatsData(overview);
      
      // Calculate total revenue from client payments
      let totalRevenueFromPayments = 0;
      const paymentsData = extractArrayData(clientPaymentsRes);
      if (paymentsData && paymentsData.length > 0) {
        totalRevenueFromPayments = paymentsData.reduce((sum: number, payment: any) => {
          const amount = Number(payment.amount) || 0;
          return sum + amount;
        }, 0);
      }

      setMetrics({
        activeEvents: overviewData.active_events || 0,
        pendingQuotations: overviewData.pending_quotations || 0,
        totalRevenue: totalRevenueFromPayments || overviewData.total_revenue || 0,
        pendingPayments: overviewData.pending_payments || 0,
        completedTasks: overviewData.completed_tasks || 0,
        totalClients: overviewData.total_clients || 0,
      });

      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch metrics:', err);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMetrics();

    refreshTimeoutRef.current = setInterval(fetchMetrics, refreshInterval);

    return () => {
      if (refreshTimeoutRef.current) {
        clearInterval(refreshTimeoutRef.current);
      }
    };
  }, [fetchMetrics, refreshInterval]);

  return { metrics, loading };
}
